/**
 * src/components/ThreeGameCanvas.jsx
 * React shell — HUD + ปุ่มมือถือ
 */
import { memo, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useThreeGame } from '../hooks/useThreeGame.js';
import { usePlayTelemetry } from '../hooks/usePlayTelemetry.js';
import MoodSurvey from './MoodSurvey.jsx';
import GameLoader from './GameLoader.jsx';
import PlayHistory from './PlayHistory.jsx';
import { BULLET_CHOICES, BULLET_MAX_COUNT } from '../three/config.js';
import { getUrlParams } from '../utils/urlParams.js';
import { lookupBrand } from '../utils/playApi.js';
import { MOODS } from '../constants/moods.js';

const SWIPE_DELETE_PX = 44;
const SWIPE_MAX_PX = 72;
const TAP_SLOP_PX = 14;

function isEditableTarget(target) {
  const el = target?.nodeType === 1 ? target : target?.parentElement;
  if (el?.closest?.('input, textarea, [contenteditable="true"], .rage-input-root')) {
    return true;
  }
  const active = document.activeElement;
  return !!active?.matches?.('input, textarea, [contenteditable="true"]')
    || !!active?.closest?.('.rage-input-root');
}

/** Android Chrome ignores React's passive touch/select listeners — bind native ones. */
function useBlockTextSelect() {
  useEffect(() => {
    const prevent = (e) => {
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
    };
    const clearSel = () => {
      if (isEditableTarget(document.activeElement)) return;
      const sel = window.getSelection?.();
      if (!sel || sel.rangeCount === 0) return;
      if (isEditableTarget(sel.anchorNode)) return;
      sel.removeAllRanges();
    };
    const clearOnTouch = (e) => {
      if (isEditableTarget(e.target) || isEditableTarget(document.activeElement)) return;
      window.getSelection?.()?.removeAllRanges?.();
    };
    const opts = { capture: true };
    const touchOpts = { capture: true, passive: false };

    document.addEventListener('contextmenu', prevent, opts);
    document.addEventListener('selectstart', prevent, opts);
    document.addEventListener('dragstart', prevent, opts);
    document.addEventListener('selectionchange', clearSel);
    document.addEventListener('gesturestart', prevent, touchOpts);
    document.addEventListener('touchstart', clearOnTouch, { capture: true, passive: true });
    document.addEventListener('touchend', clearOnTouch, { capture: true, passive: true });
    document.addEventListener('pointerdown', clearOnTouch, opts);

    return () => {
      document.removeEventListener('contextmenu', prevent, opts);
      document.removeEventListener('selectstart', prevent, opts);
      document.removeEventListener('dragstart', prevent, opts);
      document.removeEventListener('selectionchange', clearSel);
      document.removeEventListener('gesturestart', prevent, touchOpts);
      document.removeEventListener('touchstart', clearOnTouch, { capture: true, passive: true });
      document.removeEventListener('touchend', clearOnTouch, { capture: true, passive: true });
      document.removeEventListener('pointerdown', clearOnTouch, opts);
    };
  }, []);
}

function VolumeSlider({ value, onChange, label }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);

  const applyY = (clientY) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const t = 1 - (clientY - rect.top) / Math.max(1, rect.height);
    onChange(Math.max(0, Math.min(1, t)));
  };

  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);

  return (
    <div
      className="vol-pop"
      role="slider"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-orientation="vertical"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        applyY(e.clientY);
      }}
      onPointerMove={(e) => {
        if (!dragging.current) return;
        e.preventDefault();
        e.stopPropagation();
        applyY(e.clientY);
      }}
      onPointerUp={(e) => {
        dragging.current = false;
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
      }}
      onPointerCancel={() => { dragging.current = false; }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="vol-track" ref={trackRef}>
        <div className="vol-fill" style={{ height: `${pct}%` }} />
        <div className="vol-thumb" style={{ bottom: `${pct}%` }} />
      </div>
    </div>
  );
}

function ThreeGameCanvasInner() {
  useBlockTextSelect();
  const { userId } = useMemo(() => getUrlParams(), []);
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const swipeRef = useRef({
    index: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    dx: 0,
    rawX: 0,
    rawY: 0,
    axis: '',
  });
  const [swipeOffset, setSwipeOffset] = useState({ index: null, dx: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [fireHeld, setFireHeld] = useState(false);
  const [volOpen, setVolOpen] = useState(null);
  const fireBtnRef = useRef(null);
  const [boot, setBoot] = useState({ ready: false, pct: 8, label: 'กำลังโหลด...' });
  const [hud, setHud] = useState({
    score: 0,
    remaining: 0,
    remainingTotal: 0,
    wave: 1,
    locked: false,
    playing: false,
    mobile: false,
    rage: 0,
    rageMax: 100,
    berserk: false,
    combo: 0,
    releaseMsg: '',
    queueLen: 0,
    typing: false,
    muted: false,
    musicMuted: false,
    musicVolume: 0.20,
    sfxVolume: 1,
    stageClear: false,
    surveyReady: false,
    clearWave: 1,
    returnLabel: 'กลับไปเล่นเกมเดิมพัน',
    ammo: 100,
    ammoReserve: 200,
    ammoMag: 100,
    ammoUnlimited: true,
    bulletChoices: ['ไอ้เหี้ย', 'ควย!', 'เย็ดแม่', 'ไอ้สัส', 'ชิบหาย!'],
    selectedBullets: [],
    bulletFullAlert: false,
  });

  const onHud = useCallback((data) => setHud(data), []);
  const onProgress = useCallback((data) => {
    setBoot((prev) => (
      prev.ready
        ? prev
        : { ready: false, pct: data?.pct ?? prev.pct, label: data?.label || prev.label }
    ));
  }, []);
  const onReady = useCallback((game) => {
    gameRef.current = game;
    setBoot({ ready: true, pct: 100, label: 'พร้อมแล้ว' });
    const { brand } = getUrlParams();
    if (!brand) return;
    lookupBrand(brand).then((data) => {
      const link = data?.brand?.link;
      if (link) game.setBrandLink?.(link);
    });
  }, []);
  const applySession = useCallback((session) => {
    const link = session?.brandLink;
    if (link) gameRef.current?.setBrandLink?.(link);
  }, []);
  const { handlePlayEvent, submitMood, setPendingMood } = usePlayTelemetry({
    onSession: applySession,
    getSnapshot: () => gameRef.current?.getPlaySnapshot?.() || null,
  });
  useThreeGame(containerRef, { onHud, onProgress, onReady, onPlayEvent: handlePlayEvent });

  const handleMoodPick = useCallback((moodId) => {
    setPendingMood(MOODS.find((item) => item.id === moodId) || null);
  }, [setPendingMood]);

  const handleMoodContinue = useCallback((moodId) => {
    const mood = MOODS.find((item) => item.id === moodId);
    submitMood(mood, 'continue', gameRef.current?.getPlaySnapshot?.());
    gameRef.current?.nextWave();
  }, [submitMood]);

  const handleMoodReturn = useCallback((moodId) => {
    const mood = MOODS.find((item) => item.id === moodId);
    submitMood(mood, 'return', gameRef.current?.getPlaySnapshot?.());
    gameRef.current?.returnToMain();
  }, [submitMood]);

  const ragePct = Math.min(100, ((hud.rage || 0) / (hud.rageMax || 100)) * 100);
  const wallTotal = Math.max(1, hud.remainingTotal || 0);
  const cleared = Math.max(0, wallTotal - (hud.remaining || 0));
  const clearPct = Math.min(100, Math.max(0, (cleared / wallTotal) * 100));
  const playing = hud.playing || hud.locked;
  const mobile = hud.mobile;
  const showMobilePlayControls = mobile && playing && !hud.typing
    && hud.remaining > 0 && !hud.stageClear;

  useEffect(() => {
    const btn = fireBtnRef.current;
    if (!btn) return;
    const ua = navigator.userAgent || '';
    const ios = /iPhone|iPad|iPod/i.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!ios) return;
    const sw = document.createElement('input');
    sw.type = 'checkbox';
    sw.setAttribute('switch', '');
    sw.className = 'ios-haptic-switch';
    sw.setAttribute('aria-hidden', 'true');
    sw.tabIndex = -1;
    btn.appendChild(sw);
    gameRef.current?.sfx?.setIosSwitch?.(sw);
    return () => {
      sw.remove();
      gameRef.current?.sfx?.setIosSwitch?.(null);
    };
  }, [showMobilePlayControls]);

  useEffect(() => {
    if (!volOpen) return;
    const close = (e) => {
      if (e.target?.closest?.('.vol-wrap')) return;
      setVolOpen(null);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [volOpen]);

  const toggleVol = (id) => {
    gameRef.current?.ensureAudio?.();
    setVolOpen((cur) => (cur === id ? null : id));
  };

  const preventSelect = (e) => {
    e.preventDefault();
  };
  const preventUiSelect = (e) => {
    if (e.target.closest?.('input, textarea, .rage-input-root')) return;
    e.preventDefault();
  };
  const onNoSelectTap = (handler) => ({
    onContextMenu: preventSelect,
    onSelectStart: preventSelect,
    onTouchStart: preventSelect,
    onPointerDown: (e) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerUp: (e) => {
      const had = e.currentTarget.hasPointerCapture?.(e.pointerId);
      if (had) e.currentTarget.releasePointerCapture?.(e.pointerId);
      e.preventDefault();
      if (!had) return;
      if (e.button != null && e.button !== 0) return;
      const r = e.currentTarget.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
      handler(e);
    },
    onClick: preventSelect,
  });
  const onAddWordTap = {
    onContextMenu: preventSelect,
    onSelectStart: preventSelect,
    onPointerDown: (e) => {
      e.stopPropagation();
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerUp: (e) => {
      const had = e.currentTarget.hasPointerCapture?.(e.pointerId);
      if (had) e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (e.button != null && e.button !== 0) return;
      const r = e.currentTarget.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
      gameRef.current?.openRageInput();
    },
    onClick: (e) => e.preventDefault(),
  };
  const fireDown = (e) => {
    if (!e.target?.classList?.contains('ios-haptic-switch')) {
      e.preventDefault();
    }
    e.stopPropagation();
    window.getSelection?.()?.removeAllRanges?.();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFireHeld(true);
    gameRef.current?.beginAimFire(e.clientX, e.clientY);
  };
  const fireMove = (e) => {
    if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
    e.preventDefault();
    e.stopPropagation();
    gameRef.current?.moveAimFire(e.clientX, e.clientY);
  };
  const fireUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
    setFireHeld(false);
    gameRef.current?.endAimFire();
  };

  const resetSwipe = useCallback(() => {
    swipeRef.current = {
      index: null,
      pointerId: null,
      startX: 0,
      startY: 0,
      dx: 0,
      rawX: 0,
      rawY: 0,
      axis: '',
    };
    setSwipeOffset({ index: null, dx: 0 });
  }, []);

  const openDeleteConfirm = useCallback((index) => {
    const word = gameRef.current?.bulletChoices?.[index]
      ?? hud.bulletChoices?.[index];
    if (word == null) return;
    gameRef.current?.setFiring?.(false);
    gameRef.current?.endAimFire?.();
    if (document.pointerLockElement) document.exitPointerLock?.();
    setDeleteConfirm({ index, word: String(word) });
  }, [hud.bulletChoices]);

  const cancelDeleteConfirm = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  const confirmDeleteBullet = useCallback(() => {
    if (deleteConfirm == null) return;
    gameRef.current?.removeBulletChoice(deleteConfirm.index);
    setDeleteConfirm(null);
  }, [deleteConfirm]);

  const onBulletPointerDown = useCallback((e, index) => {
    if (e.button != null && e.button !== 0) return;
    if (deleteConfirm) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    swipeRef.current = {
      index,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      dx: 0,
      rawX: 0,
      rawY: 0,
      axis: '',
    };
    setSwipeOffset({ index, dx: 0 });
  }, [deleteConfirm]);

  const onBulletPointerMove = useCallback((e, index) => {
    const s = swipeRef.current;
    if (s.index !== index || s.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const rawX = e.clientX - s.startX;
    const rawY = e.clientY - s.startY;
    s.rawX = rawX;
    s.rawY = rawY;
    if (!s.axis && (Math.abs(rawX) > 8 || Math.abs(rawY) > 8)) {
      s.axis = Math.abs(rawX) >= Math.abs(rawY) ? 'x' : 'y';
    }
    if (s.axis === 'y') return;
    const dx = Math.max(-SWIPE_MAX_PX, Math.min(0, rawX));
    s.dx = dx;
    setSwipeOffset({ index, dx });
  }, []);

  const onBulletPointerCancel = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
    resetSwipe();
  }, [resetSwipe]);

  const onBulletPointerUp = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
    const s = swipeRef.current;
    const same = s.index === index;
    const dx = same ? s.dx : 0;
    const axis = same ? s.axis : '';
    const moved = same
      ? Math.max(Math.abs(s.rawX), Math.abs(s.rawY))
      : 0;
    resetSwipe();
    if (deleteConfirm) return;
    if (e.button != null && e.button !== 0) return;
    if (axis === 'x' && dx <= -SWIPE_DELETE_PX) {
      openDeleteConfirm(index);
      return;
    }
    if (axis === 'y' || moved > TAP_SLOP_PX) return;
    gameRef.current?.toggleBulletChoice(index);
  }, [deleteConfirm, openDeleteConfirm, resetSwipe]);

  const onBulletClick = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.detail !== 0) return;
    if (deleteConfirm) return;
    gameRef.current?.toggleBulletChoice(index);
  }, [deleteConfirm]);

  const bulletPickButtons = (hud.bulletChoices || []).map((word, i) => {
    const on = (hud.selectedBullets || []).includes(i);
    const custom = !(BULLET_CHOICES || []).includes(word);
    const swiping = swipeOffset.index === i && swipeOffset.dx < 0;
    const willDelete = swiping && swipeOffset.dx <= -SWIPE_DELETE_PX;
    return (
      <button
        key={`bullet-${i}-${word}`}
        type="button"
        aria-pressed={on}
        title="แตะเพื่อเลือก · ปัดซ้ายเพื่อลบ"
        className={`bullet-pick${on ? ' is-on' : ''}${custom ? ' bullet-pick--custom' : ''}${swiping ? ' is-swiping' : ''}${willDelete ? ' is-swipe-delete' : ''}`}
        style={swiping ? { transform: `translateX(${swipeOffset.dx}px)` } : undefined}
        onPointerDown={(e) => onBulletPointerDown(e, i)}
        onPointerMove={(e) => onBulletPointerMove(e, i)}
        onPointerUp={(e) => onBulletPointerUp(e, i)}
        onPointerCancel={onBulletPointerCancel}
        onTouchStart={preventSelect}
        onSelectStart={preventSelect}
        onClick={(e) => onBulletClick(e, i)}
        onContextMenu={preventSelect}
      >
        {!mobile && (
          <span className="bullet-pick-key ui-label" data-label={String(i + 1)} aria-hidden="true" />
        )}
        <span className="bullet-pick-text ui-label" data-label={word} aria-hidden="true" />
      </button>
    );
  });

  return (
    <div
      className={`three-root${hud.berserk ? ' is-berserk' : ''}${mobile ? ' is-mobile' : ''}${hud.stageClear ? ' is-stage-clear' : ''}${boot.ready ? '' : ' is-booting'}`}
      onContextMenu={preventUiSelect}
      onSelectStart={preventUiSelect}
    >
      <div
        ref={containerRef}
        id="game-container"
        className="game-container three-canvas"
        role="application"
        aria-label="ยิงกำแพง 3D"
      />

      {!hud.stageClear && (
        <>
      <div className={`crosshair${hud.berserk ? ' crosshair--hot' : ''}`} aria-hidden="true" />

      <div className="hud-bar">
        <div className="hud-left">
          <div className="hud-item">
            <span className="hud-label">คะแนน</span>
            <span className="hud-value">{hud.score}</span>
          </div>
          <div className="hud-item">
            <span className="hud-label">ด่าน</span>
            <span className="hud-value">{hud.wave}</span>
          </div>
          {hud.combo > 1 && (
            <div className="hud-item hud-combo">
              <span className="hud-label">คอมโบ</span>
              <span className="hud-value">×{hud.combo}</span>
            </div>
          )}
        </div>
        <div className="hud-meters">
          {!hud.stageClear && hud.remainingTotal > 0 && (
            <div
              className="clear-meter"
              aria-label={`คงเหลือ ${hud.remaining} ผ่านแล้ว ${Math.round(clearPct)}%`}
            >
              <div className="clear-meter-head">
                <span className="clear-meter-count">
                  คงเหลือ {hud.remaining}
                  <span className="clear-meter-total"> · {Math.round(clearPct)}%</span>
                </span>
              </div>
              <div className="clear-meter-track">
                <div
                  className={`clear-meter-fill${clearPct >= 80 ? ' clear-meter-fill--hot' : ''}`}
                  style={{ width: `${clearPct}%` }}
                />
              </div>
            </div>
          )}
          <div className="rage-meter" aria-label="เกจโมโห">
            <div className="rage-meter-label">
              {hud.berserk ? 'บ้าคลั่ง' : 'ความโมโห'}
            </div>
            <div className="rage-meter-track">
              <div
                className={`rage-meter-fill${hud.berserk ? ' rage-meter-fill--hot' : ''}`}
                style={{ width: `${ragePct}%` }}
              />
            </div>
          </div>
        </div>
        <div className="audio-btns">
          {mobile && boot.ready && userId && (
            <PlayHistory
              userId={userId}
              compact
              dismiss={!!volOpen}
              onOpen={() => setVolOpen(null)}
            />
          )}
          <button
            type="button"
            className="debug-collapse-btn"
            aria-label="ทดสอบ พังกำแพงทั้งหมด"
            title="ทดสอบ: พังกำแพงทั้งหมด"
            onContextMenu={preventSelect}
            onSelectStart={preventSelect}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              gameRef.current?.debugCollapseWall();
            }}
          >
            พังกำแพง
          </button>
          <div className={`vol-wrap${volOpen === 'music' ? ' is-open' : ''}`}>
            <button
              type="button"
              className={`mute-btn${hud.musicMuted ? ' mute-btn--off' : ''}`}
              aria-label="ระดับเสียงเพลง"
              aria-expanded={volOpen === 'music'}
              title="ระดับเสียงเพลง"
              onContextMenu={preventSelect}
              onSelectStart={preventSelect}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleVol('music');
              }}
            >
              {hud.musicMuted ? (
                <svg className="mute-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M10 4v10.5c-.6-.3-1.3-.5-2-.5-2.2 0-4 1.6-4 3.5S5.8 21 8 21s4-1.6 4-3.5V8.2l6-1.5V4l-8 1.8zm9.2 1.1-1.4 1.4 3 3 1.4-1.4-3-3zM3 4.4 4.4 3l16.6 16.6-1.4 1.4L3 4.4z"
                  />
                </svg>
              ) : (
                <svg className="mute-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M10 4v10.5c-.6-.3-1.3-.5-2-.5-2.2 0-4 1.6-4 3.5S5.8 21 8 21s4-1.6 4-3.5V8.2l6-1.5V14c-.6-.3-1.3-.5-2-.5-2.2 0-4 1.6-4 3.5s1.8 3.5 4 3.5 4-1.6 4-3.5V4l-8 1.8z"
                  />
                </svg>
              )}
            </button>
            {volOpen === 'music' && (
              <VolumeSlider
                label="ระดับเสียงเพลง"
                value={hud.musicVolume ?? 1}
                onChange={(v) => gameRef.current?.setMusicVolume(v)}
              />
            )}
          </div>
          <div className={`vol-wrap${volOpen === 'sfx' ? ' is-open' : ''}`}>
            <button
              type="button"
              className={`mute-btn${hud.muted ? ' mute-btn--off' : ''}`}
              aria-label="ระดับเสียงเอฟเฟกต์"
              aria-expanded={volOpen === 'sfx'}
              title="ระดับเสียงเอฟเฟกต์"
              onContextMenu={preventSelect}
              onSelectStart={preventSelect}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleVol('sfx');
              }}
            >
              {hud.muted ? (
                <svg className="mute-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M5 10v4h3.2L13 18.8V5.2L8.2 10H5zm9.5-1.1-1.4 1.4c.6.5 1 1.3 1 2.2s-.4 1.7-1 2.2l1.4 1.4c1-.9 1.6-2.2 1.6-3.6s-.6-2.7-1.6-3.6zm3-3-1.4 1.4C17.5 8.8 18.5 10.3 18.5 12s-1 3.2-2.4 4.3l1.4 1.4C19.4 16.1 20.7 14.2 20.7 12s-1.3-4.1-3.2-5.7zM4.2 3.8 3 5l16 16 1.2-1.2L4.2 3.8z"
                  />
                </svg>
              ) : (
                <svg className="mute-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M5 10v4h3.2L13 18.8V5.2L8.2 10H5zm9.5-1.1v6.2c1-.9 1.6-2.2 1.6-3.1s-.6-2.2-1.6-3.1zm3-3v12.2c1.9-1.6 3.2-4 3.2-6.1s-1.3-4.5-3.2-6.1z"
                  />
                </svg>
              )}
            </button>
            {volOpen === 'sfx' && (
              <VolumeSlider
                label="ระดับเสียงเอฟเฟกต์"
                value={hud.sfxVolume ?? 1}
                onChange={(v) => gameRef.current?.setSfxVolume(v)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="ammo-hud" aria-label="กระสุน">
        <span className="ammo-label">กระสุน</span>
        <div className="ammo-row">
          <span className="ammo-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 9h12l5-2.5v11L14 15H2V9zm14 1.2 3 1.5v2.6l-3 1.5v-5.6zM4 11v2h8v-2H4z" />
            </svg>
          </span>
          {hud.ammoUnlimited ? (
            <span className="ammo-value ammo-value--inf">∞</span>
          ) : (
            <span className={`ammo-value${hud.ammo <= 0 ? ' ammo-value--empty' : ''}`}>
              {hud.ammo}
              <span className="ammo-reserve">/{hud.ammoReserve}</span>
            </span>
          )}
        </div>
      </div>

      {hud.releaseMsg && !hud.stageClear && (
        <div className="release-toast" key={hud.releaseMsg + hud.combo}>
          {hud.releaseMsg}
        </div>
      )}

      {hud.combo >= 2 && playing && (
        <div
          className={`combo-display${hud.combo >= 15 ? ' combo-display--hot' : ''}${hud.combo >= 30 ? ' combo-display--max' : ''}`}
          key={`combo-${hud.combo}`}
        >
          <span className="combo-label">คอมโบ</span>
          <span className="combo-count">×{hud.combo}</span>
          <div className="combo-bar">
            <div
              className="combo-bar-fill"
              style={{
                width: `${Math.max(0, Math.min(100, ((hud.comboLeft || 0) / (hud.comboMax || 1.15)) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}

      {!hud.stageClear && hud.remaining > 0 && !showMobilePlayControls && boot.ready && (
        <div
          className={`bullet-picks${!playing ? ' bullet-picks--lobby' : ''}${hud.typing ? ' bullet-picks--typing' : ''}`}
          role="group"
          aria-label="คำกระสุนเสริม (ไม่บังคับ)"
        >
          {bulletPickButtons}
          <button
            type="button"
            className="bullet-pick bullet-pick--add"
            aria-label="เพิ่มคำกระสุน"
            title="เพิ่มคำ"
            {...onAddWordTap}
          >
            <span className="bullet-pick-add-icon ui-label" data-label="+" aria-hidden="true" />
            <span className="bullet-pick-text ui-label" data-label="เพิ่มคำ" aria-hidden="true" />
          </button>
        </div>
      )}

      {!mobile && !playing && !hud.stageClear && boot.ready && userId && (
        <PlayHistory userId={userId} />
      )}

      {!playing && !hud.typing && !hud.stageClear && boot.ready && (
        <div className="click-hint">
          {userId && (
            <p className="hint-user-id">
              <span className="hint-user-id-label">User ID</span>
              <span className="hint-user-id-value">{userId}</span>
            </p>
          )}
          <p>{mobile ? 'แตะเพื่อเริ่ม' : 'คลิกเพื่อเริ่มระบายอารมณ์'}</p>
          <p className="hint-sub">
            {mobile
              ? 'กดปุ่มยิงค้าง = ยิง · ลากบนปุ่มยิง = เลื่อนเป้า'
              : 'เมาส์ = มอง · คลิกค้าง = ยิง'}
          </p>
          <div className="hint-box">
            <p className="hint-box-title">บรรจุกระสุนคำพูด</p>
            {mobile ? (
              <>
                <p>แตะเลือกคำด้านล่างเพื่อเป็นกระสุนระบายอารมณ์ หรือเริ่มเลยโดยใช้กระสุนแบบสุ่ม หากคุณต้องการเพิ่มกระสุนคำพูดด้วยตนเอง</p>
                <p className="hint-box-note">กดปุ่ม <strong>+</strong> เพื่อสร้างกระสุนคำเอง</p>
              </>
            ) : (
              <>
                <p>คลิกเลือกคำด้านล่างเพื่อเป็นกระสุนระบายอารมณ์ หรือเริ่มเลยโดยใช้กระสุนแบบสุ่ม หากคุณต้องการเพิ่มกระสุนคำพูดด้วยตนเอง</p>
                <p className="hint-box-note">กดปุ่ม <strong>+</strong> เพื่อสร้างกระสุนคำเอง</p>
              </>
            )}
          </div>
        </div>
      )}

      {playing && hud.remaining > 0 && !hud.typing && !mobile && !hud.ammoUnlimited && (
        <div className="controls-tip" aria-hidden="true">
          <span><kbd>R</kbd> รีโหลด</span>
        </div>
      )}
        </>
      )}

      {hud.stageClear && hud.surveyReady && (
        <MoodSurvey
          wave={hud.clearWave || hud.wave}
          returnLabel={hud.returnLabel}
          onSelect={handleMoodPick}
          onContinue={handleMoodContinue}
          onReturn={handleMoodReturn}
        />
      )}

      {showMobilePlayControls && (
        <div className="mobile-controls">
          <button
            type="button"
            className="mobile-btn mobile-btn--type"
            aria-label="เพิ่มคำ"
            {...onAddWordTap}
          >
            <span className="ui-label" data-label="เพิ่มคำ" aria-hidden="true" />
          </button>
          <div
            className="bullet-picks"
            role="group"
            aria-label="คำกระสุนเสริม (ไม่บังคับ)"
          >
            {bulletPickButtons}
          </div>
          <button
            ref={fireBtnRef}
            type="button"
            className={`mobile-btn mobile-btn--fire${fireHeld ? ' is-active' : ''}`}
            aria-label="กดค้างยิง ลากเพื่อเลื่อนเป้า"
            title="กดค้างยิง · ลากเลื่อนเป้า"
            aria-pressed={fireHeld}
            onPointerDown={fireDown}
            onPointerMove={fireMove}
            onPointerUp={fireUp}
            onPointerCancel={fireUp}
            onLostPointerCapture={() => {
              setFireHeld(false);
              gameRef.current?.endAimFire();
            }}
            onTouchStart={(e) => {
              if (e.target?.classList?.contains('ios-haptic-switch')) return;
              preventSelect(e);
            }}
            onSelectStart={preventSelect}
            onContextMenu={preventSelect}
          >
            <span className="mobile-btn-fire-main" aria-hidden="true" />
            <span className="mobile-btn-fire-hint" aria-hidden="true" />
          </button>
        </div>
      )}

      {hud.berserk && <div className="berserk-vignette" aria-hidden="true" />}

      {hud.bulletFullAlert && (
        <div
          className="bullet-delete-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="bullet-full-title"
          aria-describedby="bullet-full-desc"
          onClick={() => gameRef.current?.dismissBulletFullAlert?.()}
        >
          <div
            className="bullet-delete-card"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="bullet-full-title" className="bullet-delete-title">
              กระสุนคำเต็มแล้ว
            </p>
            <p id="bullet-full-desc" className="bullet-alert-body">
              มีครบ {BULLET_MAX_COUNT} คำแล้ว กรุณาลบคำเดิมออกก่อน จึงจะเพิ่มคำใหม่ได้
            </p>
            <p className="bullet-alert-hint">ปัดซ้ายที่คำเพื่อลบ</p>
            <div className="bullet-delete-actions">
              <button
                type="button"
                className="bullet-delete-btn bullet-delete-btn--confirm"
                {...onNoSelectTap(() => gameRef.current?.dismissBulletFullAlert?.())}
              >
                <span className="ui-label" data-label="ตกลง" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div
          className="bullet-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bullet-delete-title"
          onClick={cancelDeleteConfirm}
        >
          <div
            className="bullet-delete-card"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="bullet-delete-title" className="bullet-delete-title">
              ลบกระสุนคำนี้?
            </p>
            <p className="bullet-delete-word">«{deleteConfirm.word}»</p>
            <div className="bullet-delete-actions">
              <button
                type="button"
                className="bullet-delete-btn bullet-delete-btn--cancel"
                {...onNoSelectTap(cancelDeleteConfirm)}
              >
                <span className="ui-label" data-label="ยกเลิก" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="bullet-delete-btn bullet-delete-btn--confirm"
                {...onNoSelectTap(confirmDeleteBullet)}
              >
                <span className="ui-label" data-label="ลบ" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      <GameLoader
        progress={boot.pct}
        label={boot.label}
        ready={boot.ready}
      />
    </div>
  );
}

const ThreeGameCanvas = memo(ThreeGameCanvasInner);
export default ThreeGameCanvas;
